/* The one place that talks to lumiq-core.
 *
 * No `X-Lumiq-Tenant` header, here or anywhere in `src/`. Hard rule 1: the edge sets the tenant from
 * a validated token claim, and a frontend that sends it is asking the backend to trust the browser —
 * which is the whole vulnerability. In dev the vite proxy injects it, because the proxy stands in for
 * `api-gateway`. `eslint.config.js` fails the build on a literal matching that header name.
 *
 * `Authorization: Bearer <token>` is the other half of that same claim, attached whenever
 * `shared/auth/session.ts` holds one (ADR-012's local auth). It is exactly what a token from an
 * external IdP would look like too — this file does not know or care which issuer signed it.
 */

import { clearToken, currentToken } from '@/shared/auth/session';
import { embeddedRuntime } from '@/shared/embedded';

import { isServerPath } from './serverPaths';

/** The URL a request is actually sent to.
 *
 * **Standalone, the identity.** Inside the Suite shell, a server-owned path gains the shell's API
 * prefix (`/v1/rules` -> `/lumiq/v1/rules`), because the shell is one origin for several products and
 * its gateway routes each product's API by that prefix — stripping it again before core sees the
 * request, so lumiq-core needs no change. `isServerPath` is the existing registry of what the server
 * owns, so a path that is not the API (a blob URL, a static asset) is never rewritten.
 */
export function resolveUrl(url: string): string {
  const runtime = embeddedRuntime();
  if (runtime === null) return url;
  const path = url.split('?')[0] ?? url;
  return isServerPath(path) ? `${runtime.apiBase}${url}` : url;
}

export function authHeaders(): Record<string, string> {
  const token = currentToken();
  return token === null ? {} : { Authorization: `Bearer ${token}` };
}

/* RFC 9457 Problem Details. Every backend error serialises to this, so the UI can show the server's
 * own explanation rather than inventing one. "cannot approve a verdict computed against a different
 * schema state; re-evaluate first" is actionable; "Request failed" is not. */
interface Problem {
  readonly title?: string;
  readonly status?: number;
  readonly detail?: string;
  /* RFC 9457 §3.2 extension members. The backend carries a number here when a refusal is one the
   * screen has to *act* on rather than merely display -- see `ApiError.extras`. */
  readonly [member: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;

  /** Extension members from the problem document, when the server sent any.
   *
   * **For refusals a screen has to act on, never for ones it only shows.** A 409 reading "8
   * dataset(s) came from this source" is readable by a person and unusable by a control: digging the
   * digit back out of the sentence would couple the button to the wording, so the sentence could
   * never be reworded and a translation would break it. The server states the number twice, in prose
   * and as a field, from one value.
   *
   * `unknown` values, so a reader has to narrow before using one -- a `number` here would let a
   * screen trust a shape the server never promised.
   */
  readonly extras: Readonly<Record<string, unknown>>;

  constructor(
    status: number,
    detail: string,
    extras: Readonly<Record<string, unknown>> = {},
  ) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.extras = extras;
  }
}

/* Exported because the compliance pack download reads a *body* rather than JSON and so cannot go
 * through `getOne`, while still needing the server's own problem document on a refusal — a 403 has
 * to read as a permission answer rather than as a failed download. */
export async function problemOrStatus(response: Response): Promise<never> {
  if (response.status === 401) {
    const runtime = embeddedRuntime();
    if (runtime !== null) {
      /* Inside the Suite shell the token is the shell's, so it is the shell that re-authenticates —
       * it shows its own sign-in dialog and remounts Lumiq at the same path. Clearing the token here
       * would instead route to Lumiq's `/login`, which must never appear inside the suite. */
      runtime.onSessionExpired();
    } else {
      /* An expired or revoked token is functionally no token — clear it so the app's auth guard
       * routes to /login on the next render, rather than repeating a request the server has
       * already rejected. */
      clearToken();
    }
  }
  let detail = `request failed with ${String(response.status)}`;
  let extras: Record<string, unknown> = {};
  try {
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null) {
      const problem = body as Problem;
      if (typeof problem.detail === 'string' && problem.detail.length > 0) {
        detail = problem.detail;
      }
      extras = problem;
    }
  } catch {
    /* A non-JSON error body — a proxy's HTML 502, say. The status line is all there is, and it beats
     * swallowing the failure. */
  }
  throw new ApiError(response.status, detail, extras);
}

/* Every list response is the ADR-009 envelope: `{ items, meta: { next_cursor, projected_at } }`.
 * `projected_at` is null for a direct read, which means *current* rather than unknown. */
export interface Page<T> {
  readonly items: readonly T[];
  readonly projectedAt: string | null;
  /** The cursor for the next page, or `null` on the last one.
   *
   * **Added when the audit listing became the first screen that needed it**, and the gap it closes is
   * the shape CLAUDE.md §9 keeps recording: the comment above has documented `next_cursor` as part of
   * the envelope since this helper was written, and nothing read it. Every screen so far consumed a
   * route whose cursor is always null, so a component that wanted to page had no way to and no signal
   * that it was seeing a truncation.
   *
   * `null` means the last page — never an empty string, so a caller can branch on the value rather
   * than on its truthiness and a page whose cursor is `''` cannot read as "there is more".
   */
  readonly nextCursor: string | null;
  /** Whether this page is a bounded slice of a larger listing.
   *
   * **Distinct from `nextCursor`, and the difference is which listings can say it.** A keyset route
   * answers "there is more" with a cursor; a route that is `ORDER BY … LIMIT n` with no cursor
   * cannot, and for those `truncated` is the only signal a caller gets. Reading the cursor alone
   * would make a capped listing indistinguishable from a complete one — which is the
   * `next_cursor: null`-while-truncating defect, arrived at by believing the envelope.
   *
   * **Absent means `false`, and that is safe rather than optimistic.** A route that omits the field
   * is one whose page is either complete or cursored, and both are already reported truthfully; a
   * default of `true` would put "there may be more" under every listing in the product, which is the
   * cry-wolf failure that gets a signal ignored.
   */
  readonly truncated: boolean;
}

/** Reads one resource, validating it with a generated guard rather than casting.
 *
 * The guard is not decoration. A cast would let a malformed record through with `state: undefined`,
 * and a tone lookup on `undefined` renders an empty cell — a PII classification displayed as nothing
 * at all. Refusing the response is the loud failure; rendering a blank badge is the quiet one.
 */
export async function getOne<T>(
  url: string,
  guard: (value: unknown) => value is T,
  what: string,
): Promise<T> {
  const response = await fetch(resolveUrl(url), {
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  if (!response.ok) return problemOrStatus(response);

  const body: unknown = await response.json();
  if (!guard(body)) throw new Error(`the ${what} response did not match its schema`);
  return body;
}

/** Reads a page, validating every item.
 *
 * One bad item fails the whole page. Showing eleven of twelve rows without saying so is worse than
 * showing none: the count looks plausible, so nobody goes looking for the twelfth.
 */
export async function getPage<T>(
  url: string,
  guard: (value: unknown) => value is T,
  what: string,
): Promise<Page<T>> {
  const response = await fetch(resolveUrl(url), {
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  if (!response.ok) return problemOrStatus(response);

  const body: unknown = await response.json();
  if (typeof body !== 'object' || body === null || !('items' in body)) {
    throw new Error(`the ${what} response has no items`);
  }
  const rawItems: unknown = body.items;
  if (!Array.isArray(rawItems))
    throw new Error(`the ${what} items field is not an array`);

  const items: T[] = [];
  for (const item of rawItems) {
    if (!guard(item)) throw new Error(`a ${what} item did not match its schema`);
    items.push(item);
  }

  return {
    items,
    projectedAt: readProjectedAt(body),
    nextCursor: readNextCursor(body),
    truncated: readTruncated(body),
  };
}

function readNextCursor(body: object): string | null {
  /* Opaque to the client, deliberately. The token is signed over the tenant *and* a fingerprint of
   * the query, so a screen cannot construct one and must not try — it echoes what it was given. That
   * is also why a filter change has to reset paging rather than reuse the cursor: the API answers 400
   * for a token issued against a different query, which is the property protecting the page rather
   * than an inconvenience to route around. */
  if (!('meta' in body)) return null;
  const meta: unknown = body.meta;
  if (typeof meta !== 'object' || meta === null || !('next_cursor' in meta)) return null;
  const cursor: unknown = meta.next_cursor;
  return typeof cursor === 'string' && cursor.length > 0 ? cursor : null;
}

function readTruncated(body: object): boolean {
  /* Strictly `true` counts. A route that does not emit the field, or emits something that is not a
   * boolean, is reporting nothing — and "nothing" must not become a warning on every screen. */
  if (!('meta' in body)) return false;
  const meta: unknown = body.meta;
  if (typeof meta !== 'object' || meta === null || !('truncated' in meta)) return false;
  return meta.truncated === true;
}

function readProjectedAt(body: object): string | null {
  if (!('meta' in body)) return null;
  const meta: unknown = body.meta;
  if (typeof meta !== 'object' || meta === null || !('projected_at' in meta)) return null;
  const projectedAt: unknown = meta.projected_at;
  return typeof projectedAt === 'string' ? projectedAt : null;
}

/** POSTs and validates the result.
 *
 * `Idempotency-Key` is required by every write in the platform (hard rule 7) and therefore required
 * here — an optional parameter would make the omission a runtime 428 rather than a type error.
 */
export async function post<T>(
  url: string,
  idempotencyKey: string,
  guard: (value: unknown) => value is T,
  what: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const init: RequestInit =
    body === undefined
      ? {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Idempotency-Key': idempotencyKey,
            ...authHeaders(),
            ...extraHeaders,
          },
        }
      : {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Idempotency-Key': idempotencyKey,
            'Content-Type': 'application/json',
            ...authHeaders(),
            ...extraHeaders,
          },
          body: JSON.stringify(body),
        };

  const response = await fetch(resolveUrl(url), init);
  if (!response.ok) return problemOrStatus(response);

  const result: unknown = await response.json();
  if (!guard(result)) throw new Error(`the ${what} response did not match its schema`);
  return result;
}

/** POSTs a request where one refusal status carries a **typed body rather than a problem**.
 *
 * `post` sends every non-ok response to `problemOrStatus`, which is right almost everywhere: a
 * failure's useful content is its `detail`, and flattening it into an `ApiError` keeps one error
 * path. It is wrong for a route whose refusal is *structured* — `POST /v1/policy/.../from-source`
 * answers 422 with compiler diagnostics carrying a line and a column, and an `ApiError` built from
 * that would keep the first message and discard every position, which is exactly what makes an
 * editor an editor.
 *
 * **The status stays 422**, and that is deliberate rather than a compromise. Answering 200 with a
 * "did not compile" flag would be simpler for this caller and would make a failed save look like a
 * success to everything that reads only a status — a CLI, a probe, an operator with curl.
 *
 * Narrow on purpose: exactly one `refusalStatus`, and the body must satisfy the same guard as the
 * success case. Anything else — 403, 404, 503, or a 422 whose body is a `Problem` because the
 * source never reached the compiler — still goes to `problemOrStatus`, so widening this by accident
 * is not possible without passing another status.
 */
export async function postExpectingRefusal<T>(
  url: string,
  idempotencyKey: string,
  guard: (value: unknown) => value is T,
  what: string,
  body: unknown,
  refusalStatus: number,
): Promise<T> {
  const response = await fetch(resolveUrl(url), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Idempotency-Key': idempotencyKey,
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok && response.status !== refusalStatus) return problemOrStatus(response);

  /* Cloned before parsing, because a refusal at this status may legitimately be a `Problem` — the
   * source was refused before a compiler saw it — and in that case the guard fails and the response
   * has to be re-read as one. A single `json()` call cannot be replayed. */
  const parsed: unknown = await response.clone().json();
  if (guard(parsed)) return parsed;
  if (!response.ok) return problemOrStatus(response);
  throw new Error(`the ${what} response did not match its schema`);
}

/** POSTs a creating or triggering request whose answer is 204 and therefore has no body.
 *
 * **`Idempotency-Key` is a required parameter, exactly as in `post`**, and for that helper's stated
 * reason rather than a claim about any particular route: an optional one would make the omission a
 * runtime 428 instead of a type error, so a caller cannot post without one by accident. Whether a
 * given path *demands* it is the server's business — `NON_CREATING_POST_PATHS` exempts the ones that
 * create nothing, and those accept an unnecessary key and ignore it, which is what `useLogIn`
 * already relies on.
 *
 * `put` and `remove` take none because they name their subject in the URL, so a retry is the same
 * request by construction. A POST cannot promise that in general.
 *
 * **No guard parameter, and that is the whole difference from `post`.** A 204 has nothing to
 * validate, and `response.json()` on an empty body *throws* — so routing a 204 route through `post`
 * would report a successful reset as a malformed response. A guard over nothing would be a
 * parameter every caller had to invent a value for.
 */
export async function postNoContent(
  url: string,
  idempotencyKey: string,
  body: unknown,
): Promise<void> {
  const response = await fetch(resolveUrl(url), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Idempotency-Key': idempotencyKey,
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    await problemOrStatus(response);
  }
}

/** PUTs and validates the result. **No `Idempotency-Key`, and that is the difference from `post`.**
 *
 * Hard rule 7 requires the header on every *creating or triggering* POST, and the platform's PUTs are
 * neither: each replaces a resource whose natural key is in the URL, so a duplicate request writes the
 * same row and there is nothing for a key to deduplicate. `ai-rulegen.yaml` records the reasoning where
 * it was settled — a POST arguing the same thing was refused by the `post-idempotency-key` gate,
 * because a rule that takes an argument is a rule a reviewer has to adjudicate.
 *
 * So the *absence* of the parameter is the safety property, mirroring `post`'s presence of it: a caller
 * cannot accidentally send an unkeyed POST, and cannot be asked for a key a PUT has no use for.
 */
export async function put<T>(
  url: string,
  guard: (value: unknown) => value is T,
  what: string,
  body: unknown,
): Promise<T> {
  const response = await fetch(resolveUrl(url), {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) return problemOrStatus(response);

  const result: unknown = await response.json();
  if (!guard(result)) throw new Error(`the ${what} response did not match its schema`);
  return result;
}

/** PATCH, for changing one field of a resource that already exists.
 *
 * **Distinct from `put` rather than a convenience.** A PUT replaces the whole resource, so a screen
 * that PUTs to change one field has to send back every other field it read — and if it read them
 * before somebody else changed one, it silently reverts that change. `put`'s one caller seeds its
 * form from the stored value for exactly that reason, with a comment saying so.
 *
 * A PATCH says which field moved and leaves the rest alone, which is what the two operations that
 * need it are: stopping an installed plugin, and resuming it. Neither wants to restate a grant.
 *
 * **No `Idempotency-Key`**, mirroring `put` and `remove`: the subject is named in the URL and the
 * body is the desired state rather than a delta, so a retry is the same request by construction.
 */
export async function patch<T>(
  url: string,
  guard: (value: unknown) => value is T,
  what: string,
  body: unknown,
): Promise<T> {
  const response = await fetch(resolveUrl(url), {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) return problemOrStatus(response);

  const result: unknown = await response.json();
  if (!guard(result)) throw new Error(`the ${what} response did not match its schema`);
  return result;
}

/** DELETE, for a resource whose removal is the whole of the request. Returns nothing.
 *
 * **No guard parameter and no return value**, and that is deliberate rather than an omission: every
 * DELETE in this platform answers 204 with no body, because there is nothing left to describe. A
 * helper that parsed a response would invite a route to start returning the removed object, and a
 * client that got one back would be one step from re-POSTing it as an undo — which is a *new*
 * resource with a new id and a new author.
 *
 * **No `Idempotency-Key` either**, mirroring `put`: a DELETE names its subject in the URL, so a
 * retry is the same request by construction and a key would be ceremony. A repeat of a completed
 * delete is a 404, which is the honest answer — the resource is gone.
 */
/** DELETEs and validates the body it answers with. **The body is the point.**
 *
 * `remove` below is for a DELETE that answers 204, which is most of them. This exists for one that
 * answers 200 with what it removed — so a screen renders "deleted <name>" from the *response*
 * rather than from what it believed it was deleting. That is the `Idempotency-Key` lesson at a
 * different verb: a value demanded and then discarded makes a message a claim about the request
 * instead of about the state.
 *
 * **No `Idempotency-Key`, mirroring `put`, `patch` and `remove`.** A DELETE names its target in the
 * URL, so a replay is the same statement about the same row rather than a second creation. Hard
 * rule 7 asks for the header on creating and triggering POSTs, which this is neither.
 */
export async function removeOne<T>(
  url: string,
  guard: (value: unknown) => value is T,
  what: string,
): Promise<T> {
  const response = await fetch(resolveUrl(url), {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  if (!response.ok) return problemOrStatus(response);

  const result: unknown = await response.json();
  if (!guard(result)) throw new Error(`the ${what} response did not match its schema`);
  return result;
}

export async function remove(url: string): Promise<void> {
  const response = await fetch(resolveUrl(url), {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  if (!response.ok) {
    await problemOrStatus(response);
  }
}

/* ---------------------------------------------------------------- transport-only primitives

   `getJson` and `postJson` do the authenticated request and nothing else: they attach the bearer
   token, translate a failure through `problemOrStatus`, and hand back the parsed body unvalidated.

   **They exist because ten feature modules had each re-implemented the transport locally**, and
   every one of them forgot the token — so nine screens 401'd the moment ADR-012's local auth was
   enabled, and the two that were fixed were fixed one screen at a time. The duplicated code was
   never the point; the duplicated *decision* about what a request carries was. A module that reaches
   for these gets the header, the RFC 9457 detail and the 401 -> `clearToken()` behaviour for free,
   and cannot get them wrong.

   **Prefer `getOne`/`getPage`/`post` when a type guard exists.** These two return `unknown` on
   purpose: they are the seam for a caller whose validation is its own (a non-envelope readiness
   document, a hand-rolled shape predating the generated guards), not a way to skip validating. A new
   screen with a generated guard should never need them. */

/** An authenticated GET returning the parsed body, unvalidated. See the note above. */
export async function getJson(url: string): Promise<unknown> {
  const response = await fetch(resolveUrl(url), {
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  if (!response.ok) return problemOrStatus(response);
  return response.json();
}

/** An authenticated POST returning the parsed body, unvalidated. See the note above.
 *
 * `idempotencyKey` is optional here and required by `post`, and the asymmetry is deliberate: hard
 * rule 7 governs *creating and triggering* POSTs, and two of this helper's callers are neither — a
 * chain verification and a what-if evaluation compute an answer rather than creating a row.
 * `NON_CREATING_POST_PATHS` is the server-side registry of that same distinction. A caller that is
 * creating something must pass one, and `post` is the better tool for it.
 */
export async function postJson(
  url: string,
  options: { readonly idempotencyKey?: string; readonly body?: unknown } = {},
): Promise<unknown> {
  const { idempotencyKey, body } = options;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(idempotencyKey === undefined ? {} : { 'Idempotency-Key': idempotencyKey }),
    ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    ...authHeaders(),
  };
  const response = await fetch(resolveUrl(url), {
    method: 'POST',
    headers,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) return problemOrStatus(response);
  return response.json();
}

/** A stable idempotency key per logical decision.
 *
 * Minting the key when the row renders rather than when the button is clicked is the whole point of
 * the header: `crypto.randomUUID()` inside a click handler produces a fresh key per attempt, so a
 * user double-clicking through a slow network sends two requests the server treats as unrelated.
 * Keyed per subject, a retry of the same decision carries the same key.
 *
 * `crypto.randomUUID` needs a secure context — HTTPS or localhost, which covers every deployment.
 */
const keys = new Map<string, string>();

/** The server's cap, mirrored. `shared/http.py` refuses a longer header with a 422.
 *
 * Mirrored rather than discovered: the refusal arrives as "Idempotency-Key must be at most 255
 * characters" *after* a user has pressed Save, and it is a client bug wearing a server error.
 */
const MAX_IDEMPOTENCY_KEY = 255;

/** A short, stable stand-in for a subject too long to put in a header.
 *
 * FNV-1a, and its weakness does not matter here: the *cache* is keyed on the exact subject, so two
 * different subjects always get two entries and therefore two different minted keys -- the digest
 * only shortens what travels, and a collision cannot make two subjects share a key. Not a security
 * boundary and not a content address; the artifact store already has one of those.
 */
function fingerprint(subject: string): string {
  let hash = 0x811c9dc5;
  for (let at = 0; at < subject.length; at += 1) {
    hash ^= subject.charCodeAt(at);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/** A key that is stable for one attempt and fresh for the next, and never too long to send.
 *
 * **The bound is here rather than at a call site, because a call site is where it was missed.**
 * `useAuthorFromRules` passed the whole serialised policy as the subject so that editing the rules
 * after a refusal would mint a new key -- reasonable intent, and with two rules the header passed
 * 255 characters and every save answered 422. Bounding the *minted* value while keeping the cache
 * keyed on the exact subject preserves that intent exactly and makes the failure unreachable for
 * every caller rather than for the one that hit it.
 */
export function idempotencyKeyFor(scope: string, subject: string): string {
  const cacheKey = `${scope}:${subject}`;
  const existing = keys.get(cacheKey);
  if (existing !== undefined) return existing;
  /* A UUID is 36 characters and the two separators are 2, so this is what is left for the subject
     once the scope has taken its share. */
  const budget = MAX_IDEMPOTENCY_KEY - scope.length - 38;
  const travels = subject.length <= budget ? subject : fingerprint(subject);
  const minted = `${scope}-${travels}-${crypto.randomUUID()}`;
  keys.set(cacheKey, minted);
  return minted;
}

/** Forgets a key so the next attempt is a genuinely new request.
 *
 * Needed for a form: registering two different connections from the same screen is two decisions,
 * not a retry of one, and reusing the key would make the second a replay of the first.
 */
export function forgetIdempotencyKey(scope: string, subject: string): void {
  keys.delete(`${scope}:${subject}`);
}

/** The raw bytes of a response, with its headers — for a body that is a *file* rather than JSON.
 *
 * **The one read in this file that does not parse.** `getOne` and friends validate a payload against a
 * generated guard, which is right for every JSON surface and wrong for an evidence pack: the body's
 * whole value is that it is byte-identical to what was stored, so parsing and re-serialising it would
 * break the checksum it is verified against. `ArrayBuffer` is what the server sent, unexamined.
 *
 * Here rather than in the feature because of what it must not forget. A bare `fetch` in a feature
 * attaches no bearer token — `no-restricted-globals` in `eslint.config.js` refuses one for that
 * reason, and the compliance pack download was a plain `<a href>` that 401'd on exactly this. The
 * headers come back too, because the server names the filename and repeats the checksum in them.
 */
export async function getBytes(
  url: string,
): Promise<{ readonly bytes: ArrayBuffer; readonly headers: Headers }> {
  const response = await fetch(resolveUrl(url), { headers: authHeaders() });
  if (!response.ok) return problemOrStatus(response);
  return { bytes: await response.arrayBuffer(), headers: response.headers };
}

/* RFC 6266. Two patterns rather than one alternation, because an alternation is resolved by
 * *position*: a header carrying both parameters has the plain one first, so the combined form
 * matched it and the encoded name it exists to prefer never won. */
const ENCODED_FILENAME = /filename\*=UTF-8''([^;]+)/i;
const PLAIN_FILENAME = /filename="([^"]*)"|filename=([^;]+)/i;

/** The name the server chose, sanitised, or the caller's fallback.
 *
 * The encoded parameter wins wherever there is one: a server that sends both sends `filename` as
 * the ASCII degradation of `filename*`, so taking the plain one would silently drop the characters
 * the encoded form exists to carry.
 *
 * Exported because `downloadFile` is not the only caller that needs it: the compliance pack
 * download saves its own bytes so it can verify a checksum over them, and it had its own
 * `filename="…"`-only reader — a second answer to what the server asked the file to be called, and
 * the narrower of the two, so a pack whose name needed encoding silently got a generated one.
 */
export function filenameFrom(disposition: string | null, fallback: string): string {
  const header = disposition ?? '';
  const encoded = ENCODED_FILENAME.exec(header);
  const plain = PLAIN_FILENAME.exec(header);
  return (
    saveableName(percentDecoded(encoded?.[1])) ??
    saveableName(plain?.[1]) ??
    saveableName(plain?.[2]?.trim()) ??
    fallback
  );
}

function percentDecoded(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  try {
    return decodeURIComponent(value);
  } catch {
    /* A malformed escape is a header we cannot read, not a download we should refuse — the next
     * candidate, and ultimately the caller's fallback, still names the file. */
    return undefined;
  }
}

/** The last path segment of a server-supplied name, or `undefined` if nothing survives.
 *
 * The name reaches a filesystem, and the server is entitled to be wrong about it: `../../.bashrc`
 * is a name a browser would otherwise be asked to save under. Chrome and Firefox each sanitise this
 * themselves, which is exactly why it is worth doing here — a guarantee held only by the user agent
 * is one that varies by user agent.
 */
function saveableName(value: string | undefined): string | undefined {
  const segment = (value ?? '').split(/[/\\]/).pop() ?? '';
  /* `\p{Cc}` rather than a range: a character class spelling out the C0 codepoints trips
     `no-control-regex`, and that rule is right that a literal control character in a pattern is
     usually a typo. The property escape says what is meant and covers DEL and the C1 block too. */
  const cleaned = segment.replace(/\p{Cc}/gu, '').trim();
  return cleaned === '' || cleaned === '.' || cleaned === '..' ? undefined : cleaned;
}

/** Fetches a file with the caller's credentials and hands it to the browser to save.
 *
 * **Here rather than in a feature, for the reason `getBytes` above is.** A download written as an
 * `<a href>` carries no bearer token, so the browser navigates out of the app and the user reads a
 * 401 problem document as a page. Two features shipped that same link before this existed, which is
 * the argument for the save living beside the transport that already knows how to authenticate.
 *
 * `fallbackName` applies only where the server names nothing. `Content-Disposition` wins because the
 * route is the one place that knows what the file is called; composing a name here would be a second
 * answer, and the two would disagree the first time a route changed its own.
 *
 * Returns the name it saved under, so a caller can say which file it handed over rather than
 * guessing. A refusal throws `ApiError` carrying the server's own detail — a 403 has to read as a
 * permission answer, and a download that silently does nothing is indistinguishable from one the
 * browser swallowed.
 */
export async function downloadFile(url: string, fallbackName: string): Promise<string> {
  const { bytes, headers } = await getBytes(url);
  const filename = filenameFrom(headers.get('content-disposition'), fallbackName);

  const objectUrl = URL.createObjectURL(
    new Blob([bytes], {
      type: headers.get('content-type') ?? 'application/octet-stream',
    }),
  );
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();
  } finally {
    /* An un-revoked blob URL pins the whole file in memory for the life of the document, and a
       screen a person leaves open would accumulate one per download. */
    URL.revokeObjectURL(objectUrl);
  }
  return filename;
}
